import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { SectionCard } from '@/components/section-card';
import { useConfirm } from '@/components/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { buildCacheTargetGroups, type CacheTargetModel } from '@/lib/admin-page-models';
import { usePurgeCache } from '@/lib/queries/cache';
import { AlertTriangle, Check, Database, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import type { CacheTarget } from '@/types/api';

export const Route = createFileRoute('/cache')({ component: CachePage });

function CachePage() {
  const purge = usePurgeCache();
  const [lastRun, setLastRun] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const confirm = useConfirm();
  const targets = buildCacheTargetGroups();

  async function onPurge(t: CacheTarget, options?: { confirmDanger?: boolean }) {
    if (options?.confirmDanger) {
      const ok = await confirm({
        title: '전체 캐시 초기화',
        description: '모든 공개 페이지의 ISR 캐시를 한 번에 초기화합니다. 필요한 경우에만 실행하세요.',
        confirmLabel: '전체 초기화',
        tone: 'destructive',
      });
      if (!ok) return;
    }

    setBusy(t);
    try {
      const res = await purge.mutateAsync(t);
      setLastRun(prev => ({ ...prev, [t]: res.timestamp }));
      toast.success('캐시 초기화 완료');
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <>
      <PageHeader title="캐시" description="필요한 범위만 골라 ISR 캐시를 초기화합니다" />

      <div className="space-y-5">
        <SectionCard
          title="범위별 초기화"
          description="수정한 영역만 빠르게 갱신합니다"
          bodyClassName="p-0"
        >
          <div className="divide-y divide-border">
            {targets.routine.map(target => (
              <CacheTargetRow
                key={target.key}
                target={target}
                busy={busy === target.key}
                disabled={busy !== null}
                lastRun={lastRun[target.key]}
                onPurge={() => onPurge(target.key)}
              />
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title="전체 초기화"
          description="여러 영역을 한 번에 배포했을 때만 사용합니다"
          actions={<Badge variant="destructive">주의</Badge>}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <span className="mt-0.5 h-9 w-9 rounded-md bg-destructive/10 text-destructive grid place-items-center shrink-0">
                <AlertTriangle className="h-4 w-4" strokeWidth={1.8} />
              </span>
              <div className="min-w-0">
                <div className="text-[14px] font-semibold">{targets.danger.label} 캐시 초기화</div>
                <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{targets.danger.description}</p>
                <LastRun value={lastRun[targets.danger.key]} />
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              disabled={busy !== null}
              onClick={() => onPurge(targets.danger.key, { confirmDanger: true })}
              className="self-start lg:self-center"
            >
              <RefreshCw className={busy === targets.danger.key ? 'animate-spin' : ''} />
              {busy === targets.danger.key ? '초기화 중…' : '전체 초기화'}
            </Button>
          </div>
        </SectionCard>
      </div>
    </>
  );
}

function CacheTargetRow({
  target,
  busy,
  disabled,
  lastRun,
  onPurge,
}: {
  target: CacheTargetModel;
  busy: boolean;
  disabled: boolean;
  lastRun?: string;
  onPurge: () => void;
}) {
  return (
    <div className="grid gap-3 px-5 sm:px-6 py-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
      <div className="flex items-start gap-3 min-w-0">
        <span className="mt-0.5 h-8 w-8 rounded-md bg-muted text-muted-foreground grid place-items-center shrink-0">
          <Database className="h-3.5 w-3.5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[14px] font-semibold">{target.label}</h3>
            <Badge variant="outline" className="font-mono">{target.scope}</Badge>
          </div>
          <p className="mt-1 text-[12.5px] text-muted-foreground leading-relaxed">{target.description}</p>
          <LastRun value={lastRun} />
        </div>
      </div>
      <Button variant="outline" size="sm" disabled={disabled} onClick={onPurge}>
        <RefreshCw className={busy ? 'animate-spin' : ''} />
        {busy ? '초기화 중…' : '초기화'}
      </Button>
    </div>
  );
}

function LastRun({ value }: { value?: string }) {
  if (!value) return null;
  return (
    <div className="mt-2 flex items-center gap-1.5 text-[11px] font-mono tabular-nums text-muted-foreground">
      <Check className="h-3 w-3 text-signal" strokeWidth={2.5} />
      마지막: {new Date(value).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
    </div>
  );
}
